/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
// Extend From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment';
// Internal Imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import PostTypes from '@linc-technologies/linced-common/enums/post-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import { IncidentOutcome } from 'linced-app/enums/behaviours';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  SuspensionTabs,
  SuspensionCategory,
  SuspensionState,
  SuspensionReason,
} from '@linc-technologies/linced-common/enums/suspensions';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';

import { generateSuspensionPost } from 'linced-app/utils/incidents';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class SuspensionsController extends Controller.extend(
  EditPostMixin,
) {
  @action
  setEarlyReturnDate(value) {
    this.earlyReturnDate = value;
  }

  PostTypes = PostTypes;
  SuspensionCategory = SuspensionCategory;
  SuspensionTabs = SuspensionTabs;
  SuspensionState = SuspensionState;

  // Services
  @service
  behaviourDates;

  @service
  countdownButton;

  @service
  dates;

  @service
  expulsions;

  @service
  fieldOptions;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  @tracked
  postDialogOpen = false;
  @tracked
  suspensionPost = null;
  @optionAlias('hero:behaviours:formalCautionMaximum')
  formalCautionMax;
  @optionAlias('hero:features:expulsions')
  expulsionsEnabled;
  @tracked earlyReturnDate = moment().format('YYYY-MM-DD');

  // Attrs
  // queryParams provides a binding to the routes query params.
  queryParams = ['dateFrom', 'dateTo', 'highlightedSuspension', 'selectedTab'];

  // State
  // Set the initial selected tab
  @tracked selectedTab = SuspensionTabs.PENDING;

  // dateFrom provides the stateful binding to a query param.
  @tracked
  dateFrom = moment().startOf('year').format('YYYY-MM-DD');

  // dateTo provides the stateful binding to a query param.
  @tracked
  dateTo = moment().endOf('year').format('YYYY-MM-DD');

  // today provides today's date, for comparison purposes.
  today = moment().format('YYYY-MM-DD');

  filters = [];
  @tracked
  deleteDialogOpen = false;
  @tracked
  selectedSuspension = null;
  selectedSuspensionDetails = null;
  @tracked
  suspensionDialogMode = null;
  @tracked
  suspensionDialogOpen = false;
  @tracked
  extensionDialogOpen = false;
  @tracked
  highlightedSuspension = '';
  suspensionAttachments = [];

  @tracked
  processing;

  @tracked
  createExpulsionDialogOpen;

  @tracked
  documentsToView;

  @tracked
  endSuspensionDialogOpen;

  @tracked
  requiredActionsDialogOpen;

  @tracked
  suspensionToEdit;

  @tracked
  termId;

  @computed('model.expulsions.[]')
  get peopleWithCurrentExpulsions() {
    if (isEmpty(this.model.expulsions)) {
      return [];
    }
    // Use a set just in case there is somehow multiple expulsions.
    let people = new Set();
    this.model.expulsions.forEach((expulsion) => {
      if (expulsion.stage === ExpulsionStage.NOT_EXPELLED) {
        return;
      }
      people.add(expulsion.get('learnerId.id'));
    });

    return [...people];
  }

  @computed('behaviourIncident')
  get behaviourPost() {
    if (this.behaviourIncident) {
      return this.behaviourIncident.get('post');
    }
  }

  @computed('selectedSuspension.{endDate,startDate}', 'earlyReturnDate')
  get returnDateIsValid() {
    if (isEmpty(this.selectedSuspension)) {
      return { status: true };
    }
    if (this.earlyReturnDate) {
      // cannot set early return date after initial return date or before start date
      if (
        moment(this.selectedSuspension.endDate, 'YYYY-MM-DD').isBefore(
          moment(this.earlyReturnDate, 'YYYY-MM-DD'),
        )
      ) {
        return {
          status: false,
          message:
            'Return date cannot be later than the suspensions initial end date',
        };
      }
      if (
        moment(this.selectedSuspension.startDate, 'YYYY-MM-DD').isAfter(
          moment(this.earlyReturnDate, 'YYYY-MM-DD'),
        )
      ) {
        return {
          status: false,
          message: 'Return date cannot be before the suspensions start date',
        };
      }

      if (
        !this.behaviourDates.validateIsSchoolDayOrEmpty(this.earlyReturnDate)
      ) {
        return {
          status: false,
          message: 'Return date must be a school day',
        };
      }
    }
    return {
      status: true,
    };
  }

  @computed('selectedSuspension.{endDate,startDate}', 'earlyReturnDate')
  get returnDateIsPast() {
    if (this.earlyReturnDate) {
      if (
        moment(this.earlyReturnDate, 'YYYY-MM-DD').isBefore(
          moment().startOf('day'),
        )
      ) {
        return true;
      }
    }
    return false;
  }

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed(
    'model.suspensions.{[],@each.tab}',
    'filters.@each.{field,value}',
    'selectedTab',
    'suspensionToView',
  )
  get filteredSuspensions() {
    if (isEmpty(this.model.suspensions)) {
      return [];
    }
    let filteredSuspensions = this.model.suspensions;

    filteredSuspensions = filteredSuspensions.filter((suspension) => {
      if (suspension.tab === this.selectedTab) {
        if (this.selectedTab === SuspensionTabs.FORMAL_CAUTIONS) {
          // only display active formal warnings
          return suspension.active;
        }
        return true;
      }
      return false;
    });

    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return filteredSuspensions;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(filters, 'key', 'yearLevel'),
      this.yearLevelOptions,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return filteredSuspensions.filter((row) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'yearLevel':
            return filterValue(
              row.get('learner.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
        }

        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('session.{roles.[],scopes.[]}')
  get canManageSuspensions() {
    return (
      this.roles.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE) ||
      this.permissions.meet(Scopes.LINCED_ADMIN)
    );
  }

  calculateDurationFromEarlyReturn() {
    let startDate = moment(this.selectedSuspension.startDate, 'YYYY-MM-DD');
    let endDate = moment(this.earlyReturnDate, 'YYYY-MM-DD');

    let duration = 0;
    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate, 'day')) {
      if (
        this.behaviourDates.validateIsSchoolDay(
          currentDate.format('YYYY-MM-DD'),
        )
      ) {
        duration++;
      }

      currentDate.add(1, 'day');
    }

    return duration;
  }

  deleteRecord(record) {
    record.setProperties({
      authorId: this.get('session.person'),
      code: '',
    });
    return record.destroyRecord().catch(() => {
      this.toaster.error('Error saving attendance data!');
    });
  }

  updateAttendanceForSuspension(suspension) {
    // We want to grab all attendance records from the day after
    // the early return date and clear the suspended mark from them all
    let startDate = moment(suspension.get('earlyReturnDate'), 'YYYY-MM-DD')
      .add(1, 'day')
      .format('YYYY-MM-DD');
    let endDate = suspension.get('endDate');

    this.store
      .query('attendance/record', {
        personId: suspension.get('learner.id'),
        dateFrom: startDate,
        dateTo: endDate,
      })
      .then((records) => {
        let promises = records.map((record) => {
          if (record.code === 'E') {
            return this.deleteRecord(record);
          }
        });

        return Promise.all(promises).catch(() => {
          this.toaster.error('Error updating attendance for suspension');
        });
      });
  }

  updateSuspensionPost(suspension) {
    return suspension
      .get('post')
      .then((post) => {
        if (isEmpty(post)) {
          return;
        }
        let content = generateSuspensionPost(suspension, suspension);
        post.set('content', content);
        return post.save().catch(() => {
          this.toaster.error('Error saving suspension post!');
        });
      })
      .catch(() => {
        this.toaster.error('Error fetching suspension post, please try again');
      });
  }

  yeet(suspension) {
    const expulsion = {
      authorId: this.get('session.person'),
      attachments: suspension.get('attachments'),
      caregiverMeetingDate: suspension.get('consultationDate'),
      caregivers: suspension.get('caregivers'),
      date: moment().format('YYYY-MM-DD'),
      factualDetailsOfBehavioursOfConcern: suspension.get('behaviourDetails'),
      incident: suspension.get('behaviourIncident'),
      jurisdiction: suspension.get('jurisdiction'),
      learnerId: suspension.get('learnerId'),
      nominatedSchoolPerson: suspension.get('nominatedContact'),
      reviewDate: suspension.get('reviewDate'),
      schoolId: suspension.get('schoolId'),
      suspension,
      noticeDate: moment().format('YYYY-MM-DD'),
      stage: ExpulsionStage.WRITTEN_NOTICE_OF_POTENTIAL_EXPULSION,
      suspensionEndDate: suspension.get('endDate'),
      suspensionStartDate: suspension.get('startDate'),
      title: SuspensionReason.getLabel(suspension.get('reason')),
    };

    if (suspension.get('duration') > 15) {
      expulsion.suspensionExtensionDelApproval = true;
    }

    this.store
      .createRecord('attendance/expulsion', expulsion)
      .save()
      .then(async (res) => {
        // The save pipeline reads the attachments, so wait for them to load.
        await this.expulsions.initialise(
          res,
          this.model.principal,
          this.model.suspensionLettersPage,
          this.model.suspensionPage,
        );
        try {
          await this.expulsions.expulsionSavePipeline();
        } catch (err) {
          this.toaster.error('Failed to save expulsion/letters: ' + err);
        }

        this.closeDeleteDialog();
        this.router.transitionTo('wellbeing.expulsions', {
          queryParams: { highlightedExpulsionId: res.id, openDialog: 'true' },
        });
      })
      .catch((error) => {
        this.toaster.error(error);
      });
  }

  clearSuspensionChanges(suspension) {
    // Discards the dialog's unsaved edits, including relationships like the
    // caregivers, which neither reload() nor rollbackAttributes() reverts.
    if (suspension) {
      rollbackRecord(suspension);
    }
  }

  @action
  async actionSuspension(suspension) {
    // resolve the async hasMany relationship upfront.
    this.suspensionAttachments = (await suspension.attachments).map(
      (record) => ({ record }),
    );
    this.suspensionDialogOpen = true;
    this.suspensionDialogMode = 'action';
    this.suspensionToEdit = suspension;
  }

  @action
  createExpulsion() {
    this.yeet(this.selectedSuspension);
  }

  @action
  viewExpulsion(expulsionId) {
    return this.router.transitionTo('wellbeing.expulsions', {
      queryParams: {
        highlightedExpulsionId: expulsionId,
      },
    });
  }

  @action
  closePostDialog() {
    if (this.extensionDialogOpen) {
      this.suspensionPost = null;
      this.postDialogOpen = false;
      this.documentsToView = null;
      return;
    }
    this.suspensionPost = null;
    this.postDialogOpen = false;
    this.documentsToView = null;
    this.selectedSuspension = null;
  }

  @action
  closeDeleteDialog() {
    this.endSuspensionDialogOpen = false;
    this.createExpulsionDialogOpen = false;
    this.deleteDialogOpen = false;
    this.selectedSuspension = null;
    this.countdownButton.cleanup();
  }

  @action
  closeRequiredActionsDialog() {
    this.requiredActionsDialogOpen = false;
    this.selectedSuspension = null;
  }

  @action
  closeSuspensionDialog() {
    this.clearSuspensionChanges(this.suspensionToEdit);
    this.suspensionDialogOpen = false;
    this.suspensionToEdit = null;
  }

  @action
  closeExtensionDialog() {
    this.extensionDialogOpen = false;
    this.selectedSuspension = null;
  }

  @action
  deleteSuspension() {
    this.processing = true;
    return Promise.resolve()
      .then(() => {
        if (this.selectedSuspension.get('post.id')) {
          return this.selectedSuspension
            .get('post')
            .then((post) => {
              return post.destroyRecord();
            })
            .catch(() => {
              return Promise.resolve();
            });
        }
      })
      .then(() => {
        if (!isEmpty(this.selectedSuspension.get('letterIds'))) {
          // resolve the async hasMany relationship upfront.
          return this.selectedSuspension.letters.then((letters) =>
            Promise.all(letters.map((letter) => letter.destroyRecord())),
          );
        }
      })
      .then(() => {
        if (this.selectedSuspension.get('behaviourIncident.id')) {
          return this.selectedSuspension
            .get('behaviourIncident')
            .then((incident) => {
              incident.set('outcome', IncidentOutcome.UNRESOLVED);
              return incident.save();
            })
            .catch(() => {
              return Promise.resolve();
            });
        }
      })
      .then(() => {
        return this.selectedSuspension.destroyRecord();
      })
      .then(() => {
        this.toaster.success('Suspension deleted succesfully');
      })
      .catch(() => {
        this.toaster.error('Error deleting Suspension');
      })
      .finally(() => {
        this.processing = false;
        this.closeDeleteDialog();
      });
  }

  @action
  async editSuspension(suspension) {
    // resolve the async hasMany relationship upfront.
    this.suspensionAttachments = (await suspension.attachments).map(
      (record) => ({ record }),
    );
    this.suspensionDialogOpen = true;
    this.suspensionDialogMode = 'edit';
    this.suspensionToEdit = suspension;
  }

  @action
  endSuspension() {
    let suspension = this.selectedSuspension;
    let duration = this.calculateDurationFromEarlyReturn();

    suspension.setProperties({
      duration,
      earlyReturnDate: this.earlyReturnDate,
    });
    return suspension.save().then((suspension) => {
      this.updateSuspensionPost(suspension);
      this.updateAttendanceForSuspension(suspension);
      this.closeDeleteDialog();
    });
  }

  @action
  extendSuspension(suspension) {
    this.suspensionDialogOpen = true;
    this.suspensionDialogMode = 'extend';
    this.suspensionToEdit = suspension;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      set(this, 'filters', [...this.filters, { field, value }]);
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      set(
        this,
        'filters',
        this.filters.filter((f) => f !== filter),
      );
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  onChangeDateFrom(newDate) {
    this.dateFrom = newDate;
    this.termId = null;
  }

  @action
  onChangeDateTo(newDate) {
    this.dateTo = newDate;
    this.termId = null;
  }

  @action
  openCreateExpulsionDialog(suspension) {
    this.createExpulsionDialogOpen = true;
    this.selectedSuspension = suspension;

    this.countdownButton.initialise(3);
  }

  @action
  openIncidentDetails(suspension) {
    suspension.get('behaviourIncident.post').then((post) => {
      this.postDialogOpen = true;
      this.suspensionPost = post;
    });
  }

  @action
  openSuspensionPost(suspension) {
    suspension.get('post').then((post) => {
      this.postDialogOpen = true;
      this.suspensionPost = post;
    });
  }

  @action
  openRequiredActionsDialog(suspension) {
    this.requiredActionsDialogOpen = true;
    this.selectedSuspension = suspension;
  }

  @action
  openDeleteDialog(suspension) {
    this.deleteDialogOpen = true;
    this.selectedSuspension = suspension;

    this.countdownButton.initialise(3);
  }

  @action
  openEndSuspensionDialog(suspension) {
    this.endSuspensionDialogOpen = true;
    this.selectedSuspension = suspension;
    this.earlyReturnDate = suspension.displayEndDate;
  }

  @action
  manageExtensions(suspension) {
    this.extensionDialogOpen = true;
    this.selectedSuspension = suspension;
  }

  @action
  setSelectedTermId(term) {
    this.dateFrom = term.start;
    this.dateTo = term.end;
    this.termId = term.id;
  }

  @action
  tabChanged(newTabIndex) {
    // changing tabs - reset the selected incident
    this.highlightedSuspension = '';
    if (newTabIndex === SuspensionTabs.COMPLETED_SUSPENSIONS) {
      let currentTerm = this.terms.currentTerm;
      if (currentTerm) {
        this.selectedTab = newTabIndex;
        this.dateFrom = currentTerm.start;
        this.dateTo = currentTerm.end;
        this.termId = currentTerm.id;
        return;
      }
    }
    // any tabs other than completed suspensions -> default to this year
    this.termId = null;
    this.selectedTab = newTabIndex;
    this.dateFrom = moment().startOf('year').format('YYYY-MM-DD');
    this.dateTo = moment().endOf('year').format('YYYY-MM-DD');
  }

  @action
  viewDocuments(suspension) {
    this.documentsToView = suspension.allLetters;
    this.postDialogOpen = true;
    this.selectedSuspension = suspension;
  }

  @action
  viewExtensionDocuments(documentsToView) {
    this.documentsToView = documentsToView;
    this.postDialogOpen = true;
  }
}
